import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import {
  MagnifyingGlass,
  Plus,
  DownloadSimple,
  PawPrint,
  Cat,
  Dog,
  ArrowRight,
  ClockCounterClockwise,
  Users,
  FileText,
} from "@phosphor-icons/react";
import NewPatientDialog from "@/components/NewPatientDialog";
import NewConsultationDialog from "@/components/NewConsultationDialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFmt";

function speciesIcon(sp) {
  const s = (sp || "").toLowerCase();
  if (s === "cat") return <Cat size={18} weight="fill" className="text-pf-accent" />;
  if (s === "dog") return <Dog size={18} weight="fill" className="text-pf-primary" />;
  return <PawPrint size={18} weight="fill" className="text-pf-text-secondary" />;
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [consults, setConsults] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openNewConsult, setOpenNewConsult] = useState(false);
  const navigate = useNavigate();

  async function loadAll(search = "") {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/patients", { params: search ? { q: search, limit: 20 } : { limit: 20 } }),
        api.get("/consultations", { params: { limit: 8 } }),
      ]);
      setPatients(pRes.data);
      setConsults(cRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const h = setTimeout(() => loadAll(q), 200);
    return () => clearTimeout(h);
  }, [q]);

  // Cmd+K focus
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const drafts = consults.filter((c) => c.status === "draft").length;
  const weekConsults = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    return consults.filter((c) => new Date(c.created_at) >= cutoff).length;
  }, [consults]);

  function newConsultFromDashboard() {
    // Always ask which patient the consultation is for — never auto-pick the
    // most recently accessed patient.
    setOpenNewConsult(true);
  }

  async function exportXlsx() {
    try {
      const res = await api.get("/reports/export", { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pawfile-consultations.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-pf-text">
            {t("goodEvening")}.
          </h1>
        </div>
      </div>

      {/* Search bar */}
      <div className="pf-card p-2 flex items-center gap-2 mb-6">
        <MagnifyingGlass size={20} className="ml-2 text-pf-text-secondary shrink-0" />
        <input
          id="global-search"
          data-testid="dashboard-search-input"
          type="text"
          placeholder={t("searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 bg-transparent outline-none px-1 py-2 text-base sm:text-lg placeholder:text-gray-400"
        />
        <kbd className="hidden sm:inline-block text-[10px] font-mono text-pf-text-secondary bg-pf-bg border border-pf-border rounded px-1.5 py-0.5">
          ⌘K
        </kbd>
      </div>

      {/* Metric cards + quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-6">
        <div className="md:col-span-3 pf-card p-5">
          <div className="flex items-center justify-between">
            <div className="pf-label">{t("metricPatients")}</div>
            <Users size={18} className="text-pf-primary" />
          </div>
          <div className="mt-2 text-3xl font-display font-bold text-pf-text" data-testid="metric-patients">
            {patients.length}
          </div>
        </div>
        <div className="md:col-span-3 pf-card p-5">
          <div className="flex items-center justify-between">
            <div className="pf-label">{t("metricConsultsToday")}</div>
            <ClockCounterClockwise size={18} className="text-pf-accent" />
          </div>
          <div className="mt-2 text-3xl font-display font-bold text-pf-text" data-testid="metric-week">
            {weekConsults}
          </div>
        </div>
        <div className="md:col-span-3 pf-card p-5">
          <div className="flex items-center justify-between">
            <div className="pf-label">{t("metricPendingDrafts")}</div>
            <FileText size={18} className="text-pf-warning" />
          </div>
          <div className="mt-2 text-3xl font-display font-bold text-pf-text" data-testid="metric-drafts">
            {drafts}
          </div>
        </div>

        {/* Quick actions */}
        <div className="md:col-span-3 pf-card p-5 flex flex-col gap-2 justify-center">
          <div className="pf-label mb-1">{t("quickActions")}</div>
          <button data-testid="new-patient-btn" onClick={() => setOpenNew(true)} className="pf-btn-primary justify-center">
            <Plus size={16} weight="bold" /> {t("newPatient")}
          </button>
          <button data-testid="new-consult-btn" onClick={newConsultFromDashboard} className="pf-btn-ghost justify-center">
            <FileText size={16} /> {t("newConsultation")}
          </button>
          <button data-testid="export-btn" onClick={exportXlsx} className="pf-btn-ghost justify-center">
            <DownloadSimple size={16} /> {t("exportConsults")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Recent Patients */}
        <div className="lg:col-span-7 pf-card overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <div className="pf-section-title">{t("recentPatients")}</div>
            <Link to="/patients" className="text-sm text-pf-primary hover:underline flex items-center gap-1">
              {t("patients")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-pf-border">
            {patients.length === 0 && (
              <div className="p-6 text-sm text-pf-text-secondary">{t("noResults")}</div>
            )}
            {patients.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                to={`/patients/${p.id}`}
                data-testid={`patient-row-${p.id}`}
                className="flex items-center gap-4 px-5 py-3 hover:bg-pf-bg transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-pf-primary-light grid place-items-center shrink-0">
                  {speciesIcon(p.species)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-pf-text">{p.name}</span>
                    <span className="pf-chip capitalize">
                      {p.species === "dog" ? t("dog") : p.species === "cat" ? t("cat") : p.species}
                      {p.breed ? ` · ${p.breed}` : ""}
                    </span>
                  </div>
                  <div className="text-xs text-pf-text-secondary truncate">
                    {p.owner?.full_name} {p.owner?.phone ? `· ${p.owner.phone}` : ""}
                  </div>
                  {p.recent_diagnoses && p.recent_diagnoses.length > 0 && (
                    <div className="text-[11px] text-pf-primary mt-0.5 truncate">
                      {p.recent_diagnoses.slice(0, 3).join(" · ")}
                    </div>
                  )}
                </div>
                <ArrowRight size={16} className="text-pf-text-secondary" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent consultations */}
        <div className="lg:col-span-5 pf-card overflow-hidden">
          <div className="px-5 pt-4 pb-3 pf-section-title">{t("recentConsultations")}</div>
          <div className="divide-y divide-pf-border">
            {consults.length === 0 && (
              <div className="p-6 text-sm text-pf-text-secondary">{t("noResults")}</div>
            )}
            {consults.slice(0, 6).map((c) => (
              <Link
                key={c.id}
                to={`/consultations/${c.id}`}
                data-testid={`consult-row-${c.id}`}
                className="block px-5 py-3 hover:bg-pf-bg transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-pf-text truncate">
                    {c.patient_name || "Consultation"}
                  </div>
                  {c.status === "draft" ? (
                    <span className="pf-chip text-pf-warning bg-pf-warning-bg border-pf-warning/30">draft</span>
                  ) : (
                    <span className="pf-chip-primary">final</span>
                  )}
                </div>
                <div className="text-xs text-pf-text-secondary mt-0.5">
                  {formatDate(c.created_at)} · {c.attending_vet_name || "—"}
                </div>
                <div className="text-xs text-pf-text-secondary truncate mt-0.5">
                  {(c.visit_reasons || []).join(", ") || "—"}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <NewPatientDialog open={openNew} onOpenChange={setOpenNew} onCreated={(p) => {
        setOpenNew(false);
        navigate(`/patients/${p.id}`);
      }} />
      <NewConsultationDialog open={openNewConsult} onOpenChange={setOpenNewConsult} />
    </div>
  );
}
